<template>
    <div class="auth-page">
        <div class="auth-card">
            <div class="auth-card__body">
                <h1 class="auth-title">Change Password</h1>
                <template v-if="resetCode">
                    <StatusBar :errorMessage="user.passwordResetErrorMessage" :isLoading="isLoading"
                        loadingMessage="Resetting Password..." />
                    <p class="auth-copy">
                        Welcome, {{ username }}, please use this form to change your password. New passwords are
                        required
                        to
                        be a between 6 and 32 characters in length
                    </p>
                    <input class="auth-input" data-val="true" data-val-required="The Password field is required."
                        id="password" name="New Password" placeholder="New Password" type="password"
                        v-model.lazy="password" />
                    <div v-if="passwordError" class="auth-error">{{ passwordError }}</div>
                    <input class="auth-input" data-val="true" data-val-required="The Password field is required."
                        id="confirmPassword" name="Confirm Password" placeholder="Confirm Password" type="password"
                        v-model.lazy="confirmPassword" />
                    <div v-if="confirmPasswordErrorMsg" class="auth-error">{{ confirmPasswordErrorMsg }}</div>
                    <div class="auth-actions">
                        <button type="button" @click="handleSubmitNewPassword" class="auth-btn auth-btn--compact">Submit</button>
                    </div>
                </template>
                <template v-if="!isResendSuccess && isResetSuccess">
                    <div>
                        <p class="auth-copy">
                            Your password has been changed successfully.
                        </p>
                        <div class="auth-actions">
                            <button type="button" @click="router.push('/')" class="auth-btn auth-btn--compact">Login</button>
                        </div>
                    </div>
                </template>
                <template v-if="isResendSuccess && !isResetSuccess">
                    <div>
                        <p class="auth-copy">
                            An email has been sent to {{ userRequestingResetPassword }}. Please follow the instructions in the email
                            to reset your password.
                        </p>
                        <div class="auth-actions">
                            <button type="button" @click="router.push('/')" class="auth-btn auth-btn--compact">Back</button>
                        </div>
                    </div>
                </template>
                <template v-if="!isResendSuccess && !resetCode && !isResetSuccess">
                    <div>
                        <StatusBar :errorMessage="user.passwordResetErrorMessage" :isLoading="isLoading"
                            loadingMessage="Sending ResetPasswordEmail..." />
                        <p class="auth-copy">Enter your username in the field below to submit a change password request.</p>
                        <input class="auth-input" data-val="true" data-val-required="The Password field is required."
                            id="username" name="UserName" placeholder="UserName" type="email" v-model.lazy="username" />
                        <div v-if="usernameError" class="auth-error">{{ usernameError }}</div>

                        <div class="auth-actions">
                            <button type="button" @click="router.back()" class="auth-link">Cancel</button>
                            <button type="button" @click="handleResendResetPasswordEmail" class="auth-btn auth-btn--compact">Submit</button>
                        </div>
                    </div>
                </template>
            </div>
        </div>
    </div>
</template>

<script lang="ts" setup>
import { computed, ref } from 'vue';
import router from '@/router';
import StatusBar from '@/components/form-fields/StatusBar/StatusBar.vue';
import { useRoute } from 'vue-router';
import { sendPasswordResetEmail, submitNewPassword, getPasswordResetData } from '@/helpers/api/user';
import { onBeforeMount } from 'vue';
import useUserStore from '@/stores/user';
const user = useUserStore();

const route = useRoute();

const password = ref("");
const confirmPassword = ref("");

const isResendSuccess = computed(() => route.query.isResendSuccess === "true");
const isResetSuccess = computed(() => route.query.isResetSuccess === "true");
const userRequestingResetPassword = computed(() => route.query.userRequestingResetPassword);
const resetCode = computed(() => route.query.ResetCode);
const username = ref(userRequestingResetPassword.value ? String(userRequestingResetPassword.value) : "");
const userModel = ref({})
const isLoading = ref(false);

const usernameError = computed(() => {
    if (username.value === '') {
        return 'The Username field is required.';
    }
    // if not email 
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(username.value)) {
        return 'The Username field must be a valid email address.';
    }
    return '';
});

const isStartedPasswordValidation = ref(false);
const passwordError = computed(() => {
    if (!isStartedPasswordValidation.value) {
        return '';
    }
    if (password.value === '') {
        return 'The Password field is required.';
    }
    if (password.value.length < 6) {
        return 'The Password field must be at least 6 characters.';
    }
    if (password.value.length > 32) {
        return 'The Password field must be at most 32 characters.';
    }
    return '';
});

const confirmPasswordErrorMsg = computed(() => {
    if (!isStartedPasswordValidation.value) {
        return '';
    }
    if (confirmPassword.value === '') {
        return 'The Confirm Password field is required.';
    }
    if (confirmPassword.value !== password.value) {
        return 'The Confirm Password field must match the Password field.';
    }
    return '';
});

async function handleSubmitNewPassword() {
    try {
        isStartedPasswordValidation.value = true;
        if (password.value && confirmPassword.value) {
            isLoading.value = true;
            await submitNewPassword({
                newPassword: password.value,
                confirmPassword: confirmPassword.value,
                resetCode: String(resetCode.value),
                user: userModel.value

            });
            router.push({ name: 'reset-password', query: { isResetSuccess: "true" } });
            isLoading.value = false;
            isStartedPasswordValidation.value = false;

        }
    } catch (error: any) {
        isLoading.value = false;
        console.error('handleResetPassword', error);
        user.passwordResetErrorMessage = error?.message || "";
    }

}

onBeforeMount(async () => {
    if (resetCode.value) {
        const res = await getPasswordResetData(String(resetCode.value))
        username.value = res?.user?.username;
        userModel.value = res?.user;
    }
})

async function handleResendResetPasswordEmail() {
    try {
        if (!usernameError.value) {
            isLoading.value = true;
            await sendPasswordResetEmail(username.value);
            router.push({ name: 'reset-password', query: { userRequestingResetPassword: username.value, isResendSuccess: "true" } });
            isLoading.value = false;
        }
    } catch (error: any) {
        isLoading.value = false;
        console.error('handleSendPasswordResetEmail', error);
        router.push({ name: 'reset-password', query: { userRequestingResetPassword: username.value, isResendSuccess: "false"} });
        user.passwordResetErrorMessage = error?.message || "";
    }
}

</script>

<style lang="scss" scoped>
@import '@/assets/styles/bui-auth-page';
</style>